import { useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles } from "@/mocks/articles";

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const article = articles.find((a) => a.id === Number(id));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，你查找的文章不存在。" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 pb-16 px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading text-foreground-900">文章未找到</h1>
            <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: {
        "@type": "Organization",
        name: "黑料吃瓜",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "首页",
          item: "https://www.heiliaochigua.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: article.category,
          item: `https://www.heiliaochigua.com/category/${article.categorySlug}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: article.title,
        },
      ],
    },
  ];

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <SEO
        title={`${article.title} - 黑料吃瓜`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={`https://www.heiliaochigua.com/article/${id}`}
        ogType="article"
        schema={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <article>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  {article.isHot && (
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-bold">
                      HOT
                    </span>
                  )}
                </div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 leading-snug mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views >= 10000
                      ? `${(article.views / 10000).toFixed(1)}万次播放`
                      : `${article.views}次播放`}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {article.comments}条评论
                  </span>
                </div>
              </div>

              <div className="w-full aspect-video rounded-xl overflow-hidden mb-8 bg-background-900">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  title={`黑料吃瓜视频 - ${article.title}`}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-700 leading-relaxed mb-10"
                dangerouslySetInnerHTML={{ __html: article.content }}
              ></div>

              <div className="flex flex-wrap items-center gap-2 mb-10">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="border-t border-background-200/70 pt-8">
                <h3 className="text-lg font-heading text-foreground-900 mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="w-full aspect-video rounded-lg overflow-hidden mb-3">
                        <img
                          src={ra.thumbnail}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-1">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}